/**
 * TanStack Query hooks — one per endpoint. staleTime is tuned so an operator
 * flipping between pages doesn't re-hit a self-hosted backend needlessly, while
 * still refreshing often enough for an ops dashboard.
 */
import { useQuery } from "@tanstack/react-query";

import {
  fetchConsumptionDaily,
  fetchForecast,
  fetchHealth,
  fetchKpis,
  fetchNrwDaily,
} from "@/lib/api";
import { thresholds } from "@/lib/thresholds";

const MINUTE = 60_000;

type Loc = string | null;

/** Query key registry — keeps invalidation call-sites consistent. */
export const queryKeys = {
  health: ["health"] as const,
  kpis: (locality: Loc) => ["kpis", locality] as const,
  consumptionDaily: (days: number, locality: Loc) =>
    ["consumption", "daily", days, locality] as const,
  nrwDaily: (days: number) => ["nrw", "daily", days] as const,
  forecast: (horizon: number, locality: Loc) =>
    ["forecast", horizon, locality] as const,
};

export function useHealth() {
  return useQuery({
    queryKey: queryKeys.health,
    queryFn: ({ signal }) => fetchHealth(signal),
    staleTime: 1 * MINUTE,
    refetchInterval: 2 * MINUTE,
  });
}

export function useKpis(locality: Loc = null) {
  return useQuery({
    queryKey: queryKeys.kpis(locality),
    queryFn: ({ signal }) => fetchKpis(locality, signal),
    staleTime: 5 * MINUTE,
  });
}

export function useConsumptionDaily(
  days = thresholds().default_analysis_days,
  locality: Loc = null,
) {
  return useQuery({
    queryKey: queryKeys.consumptionDaily(days, locality),
    queryFn: ({ signal }) => fetchConsumptionDaily(days, locality, signal),
    staleTime: 10 * MINUTE,
  });
}

export function useNrwDaily(days = thresholds().default_analysis_days) {
  return useQuery({
    queryKey: queryKeys.nrwDaily(days),
    queryFn: ({ signal }) => fetchNrwDaily(days, signal),
    staleTime: 10 * MINUTE,
  });
}

export function useForecast(horizon = thresholds().forecast_days, locality: Loc = null) {
  return useQuery({
    queryKey: queryKeys.forecast(horizon, locality),
    queryFn: ({ signal }) => fetchForecast(horizon, locality, signal),
    staleTime: 10 * MINUTE,
  });
}
